import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listReferralClients,
  getReferralTree,
  getReferralForest,
  setClientReferrer,
  setClientReferrerBulk,
  listReferralAuditLog,
  listReferralCandidates,
  adminResendConfirmation,
  getReferralGroups,
  listReferralInvites,
  sendReferralInvite,
  getReferralRevenue,
  getReferralSummaryStats,
  type ClientRow,
  type TreeNode,
  type ForestNode,
  type ReferralCandidate,
} from "@/lib/admin-referral-tree.functions";
import { describeReferralError } from "@/lib/admin-referral-errors";
import { getUserActivationDiagnostics } from "@/lib/activation-diagnostics.functions";
import { getStripeEnvironment } from "@/lib/stripe";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowUpDown, Layers, MailPlus, Coins, Download, Users, GitBranch, History, Network, ChevronRight, ChevronDown, Wand2, MailCheck, MailWarning, Send, UserSearch, Percent, Copy, Check, AlertTriangle } from "lucide-react";
import { ReferrerMessagesCard, ReferrerContributionsCard } from "@/components/admin/ReferrerActivityCards";
import { StripeSyncCard } from "@/components/admin/StripeSyncCard";

import { toast } from "sonner";

/** Rychlé přiřazení doporučitele přímo v seznamu nepřiřazených uživatelů. */
function AssignInline({ onAssign, pending }: { onAssign: (referrerId: string) => void; pending: boolean }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const valid = /^[0-9a-fA-F-]{36}$/.test(value.trim());

  if (!open) {
    return (
      <Button size="sm" variant="outline" className="mt-1 h-7 w-full text-xs" onClick={() => setOpen(true)}>
        Přiřadit k doporučiteli
      </Button>
    );
  }
  return (
    <div className="mt-1 flex items-center gap-1">
      <Input
        autoFocus
        placeholder="ID doporučitele (UUID)"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="h-7 font-mono text-[11px]"
      />
      <Button
        size="sm"
        className="h-7 text-xs"
        disabled={!valid || pending}
        onClick={() => {
          onAssign(value.trim());
          setOpen(false);
          setValue("");
        }}
      >
        OK
      </Button>
      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setOpen(false)}>
        Zrušit
      </Button>
    </div>
  );
}


function UserId({ id }: { id: string | null | undefined }) {
  if (!id) return <span className="text-muted-foreground">—</span>;
  const [copied, setCopied] = useState(false);
  const handleCopy = async (e?: React.MouseEvent | React.KeyboardEvent) => {
    e?.stopPropagation();
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      toast.success("ID zkopírováno");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Kopírování selhalo");
    }
  };
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={handleCopy}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleCopy(); } }}
      className="inline-flex cursor-pointer items-center gap-1 rounded px-1 py-0.5 font-mono text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground"
      title={id}
    >
      {id}
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
    </span>
  );
}




export const Route = createFileRoute("/admin/doporuceni")({
  head: () => ({
    meta: [
      { title: "Strom doporučení — administrace" },
      {
        name: "description",
        content:
          "Přehled klientů, jejich zařazení, strom doporučení, uživatelé bez doporučitele a audit log změn.",
      },
      { property: "og:title", content: "Strom doporučení — administrace" },
      {
        property: "og:description",
        content: "Struktura klientů, doporučitelé, počty vacantů a auditní záznamy.",
      },
    ],
  }),
  component: AdminReferralsPage,
});

type SortKey = "name" | "email" | "vakanty" | "created" | "visits" | "first_visit" | "revenue";
type SourceFilter = "any" | "app" | "chat" | "email" | "referral";
type VisitFilter = "any" | "visited" | "never";

/** Uživatel "for direct" — virtuální doporučitel pro účty získané mimo referral link. */
const FOR_DIRECT_REFERRER_ID = "a8347ad4-e826-4e2a-9286-f079d572f13e";

function AdminReferralsPage() {
  const qc = useQueryClient();
  const list = useServerFn(listReferralClients);
  const tree = useServerFn(getReferralTree);
  const setReferrer = useServerFn(setClientReferrer);
  const bulkFn = useServerFn(setClientReferrerBulk);
  const auditFn = useServerFn(listReferralAuditLog);

  const [q, setQ] = useState("");
  const [sort, setSort] = useState<SortKey>("name");
  const [dir, setDir] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [onlyNoReferrer, setOnlyNoReferrer] = useState(false);
  const [referrerFilter, setReferrerFilter] = useState<string | null>(null);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("any");
  const [visitFilter, setVisitFilter] = useState<VisitFilter>("any");
  const [minRevenue, setMinRevenue] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [referrerInput, setReferrerInput] = useState("");
  const [forestQ, setForestQ] = useState("");
  const [expandAll, setExpandAll] = useState(false);
  const [bulkIds, setBulkIds] = useState<Set<string>>(new Set());
  const [bulkReferrer, setBulkReferrer] = useState("");


  const forestFn = useServerFn(getReferralForest);
  const forest = useQuery({
    queryKey: ["admin", "referral-forest"],
    queryFn: () => forestFn({ data: {} }),
  });

  const visibleRoots = useMemo(() => {
    if (!forest.data) return null;
    const q = forestQ.trim().toLowerCase();
    if (!q) return forest.data.roots;
    const match = (n: ForestNode) =>
      (n.full_name ?? "").toLowerCase().includes(q) || (n.email ?? "").toLowerCase().includes(q);
    const filter = (n: ForestNode): ForestNode | null => {
      const kids = n.children.map(filter).filter(Boolean) as ForestNode[];
      if (!match(n) && kids.length === 0) return null;
      return { ...n, children: kids };
    };
    return forest.data.roots.map(filter).filter(Boolean) as ForestNode[];
  }, [forest.data, forestQ]);

  // Kořeny bez potomků = uživatelé, kteří nejsou nikomu přiřazeni (ani nemají vlastní strukturu).
  const structureRoots = useMemo(
    () => (visibleRoots ?? []).filter((n) => n.children.length > 0),
    [visibleRoots],
  );
  const unassignedRoots = useMemo(
    () => (visibleRoots ?? []).filter((n) => n.children.length === 0),
    [visibleRoots],
  );


  const clients = useQuery({
    queryKey: [
      "admin", "referral-clients", q, sort, dir, page, pageSize, onlyNoReferrer, referrerFilter,
      sourceFilter, visitFilter, minRevenue,
    ],
    queryFn: () =>
      list({
        data: {
          q, sort, dir, page, pageSize, onlyNoReferrer,
          referrerId: referrerFilter,
          source: sourceFilter,
          visitState: visitFilter,
          minRevenueCents: Math.round((Number(minRevenue.replace(",", ".")) || 0) * 100),
        },
      }),
  });

  // Serverová volání občas selžou přechodně (deploy / reload modulu) — raději
  // to zkusíme znovu, než abychom uživateli ukázali prázdnou stránku.
  const resilient = {
    retry: 3,
    retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 8000),
  } as const;

  const forDirect = useQuery({
    queryKey: ["admin", "referral-clients", "for-direct"],
    queryFn: () =>
      list({
        data: {
          q: "",
          sort: "name",
          dir: "asc",
          page: 1,
          pageSize: 200,
          onlyNoReferrer: false,
          referrerId: FOR_DIRECT_REFERRER_ID,
          source: "any",
          visitState: "any",
          minRevenueCents: 0,
        },
      }),
    ...resilient,
  });

  const treeQuery = useQuery({
    queryKey: ["admin", "referral-tree", selected],
    queryFn: () => tree({ data: { userId: selected!, depth: 3 } }),
    enabled: !!selected,
  });

  const activationDiag = useQuery({
    queryKey: ["admin", "activation-diagnostics", selected],
    queryFn: () =>
      getUserActivationDiagnostics({
        data: { environment: getStripeEnvironment(), userId: selected! },
      }),
    enabled: !!selected,
    staleTime: 30_000,
  });

  const audit = useQuery({
    queryKey: ["admin", "referral-audit"],
    queryFn: () => auditFn({ data: { limit: 50 } }),
  });

  function fmtMoney(cents: number) {
    return `${(cents / 100).toLocaleString("cs-CZ", { maximumFractionDigits: 2 })} €`;
  }

  function invalidateAll() {
    qc.invalidateQueries({ queryKey: ["admin", "referral-clients"] });
    qc.invalidateQueries({ queryKey: ["admin", "referral-tree"] });
    qc.invalidateQueries({ queryKey: ["admin", "referral-audit"] });
    qc.invalidateQueries({ queryKey: ["admin", "referral-forest"] });
    qc.invalidateQueries({ queryKey: ["admin", "referral-candidates"] });
    qc.invalidateQueries({ queryKey: ["admin", "referral-groups"] });
    qc.invalidateQueries({ queryKey: ["admin", "referral-invites"] });
    qc.invalidateQueries({ queryKey: ["admin", "referral-revenue"] });
    qc.invalidateQueries({ queryKey: ["admin", "referral-summary-stats"] });
  }

  const assign = useMutation({
    mutationFn: (vars: { userId: string; referrerId: string | null }) =>
      setReferrer({ data: vars }),
    onSuccess: (_res, vars) => {
      toast.success(vars.referrerId ? "Doporučitel byl nastaven." : "Doporučitel byl odebrán.");
      setReferrerInput("");
      invalidateAll();
    },
    onError: (e: Error) => toast.error(describeReferralError(e)),
  });

  const bulkAssign = useMutation({
    mutationFn: (vars: { userIds: string[]; referrerId: string | null }) =>
      bulkFn({ data: vars }),
    onSuccess: (res) => {
      const failures = res.results.filter((r) => !r.ok);
      if (failures.length) {
        toast.error(
          `Hromadná změna: ${res.changed} upraveno, ${res.failed} selhalo. První chyba — ${describeReferralError(failures[0]!.error)}`,
        );
      } else {
        toast.success(
          `Hromadná změna hotova: ${res.changed} upraveno, ${res.unchanged} beze změny.`,
        );
      }
      setBulkIds(new Set());
      setBulkReferrer("");
      invalidateAll();
    },
    onError: (e: Error) => toast.error(describeReferralError(e)),
  });


  const total = clients.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  function toggleSort(key: SortKey) {
    if (sort === key) setDir(dir === "asc" ? "desc" : "asc");
    else {
      setSort(key);
      setDir("asc");
    }
    setPage(1);
  }

  async function exportCsv() {
    try {
      const all = await list({
        data: { q, sort, dir, page: 1, pageSize: 200, onlyNoReferrer, referrerId: referrerFilter },
      });
      const header = [
        "jmeno",
        "email",
        "vacanty",
        "doporucitel",
        "primych_doporucenych",
        "zarazeni",
        "registrace",
      ];
      const esc = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      const lines = [
        header.join(";"),
        ...all.rows.map((r: ClientRow) =>
          [
            esc(r.full_name),
            esc(r.email),
            esc(r.vakanty_purchased),
            esc(r.referrer_name ?? "—"),
            esc(r.direct_children),
            esc(r.roles.join(", ")),
            esc(r.created_at.slice(0, 10)),
          ].join(";"),
        ),
      ];
      const blob = new Blob(["\uFEFF" + lines.join("\n")], {
        type: "text/csv;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `klienti-doporuceni-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exportováno ${all.rows.length} záznamů.`);
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  const stats = useMemo(
    () => ({
      total: clients.data?.totalAll ?? 0,
      noReferrer: clients.data?.totalNoReferrer ?? 0,
      withReferrer: clients.data?.stats?.withReferrer ?? 0,
      referredPct: clients.data?.stats?.referredPct ?? 0,
      emailConfirmed: clients.data?.stats?.emailConfirmed ?? 0,
    }),
    [clients.data],
  );

  const candidatesFn = useServerFn(listReferralCandidates);
  const candidates = useQuery({
    queryKey: ["admin", "referral-candidates"],
    queryFn: () => candidatesFn({ data: {} }),
    ...resilient,
  });

  const groupsFn = useServerFn(getReferralGroups);
  const [groupQ, setGroupQ] = useState("");
  const groups = useQuery({
    queryKey: ["admin", "referral-groups", groupQ],
    queryFn: () => groupsFn({ data: { q: groupQ } }),
    ...resilient,
  });
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());

  const invitesFn = useServerFn(listReferralInvites);
  const invites = useQuery({
    queryKey: ["admin", "referral-invites"],
    queryFn: () => invitesFn({ data: { limit: 50 } }),
  });

  const inviteReferrerOptions = useMemo(() => {
    const map = new Map<string, { id: string; name: string; email: string | null }>();
    for (const g of groups.data?.groups ?? []) {
      if (g.referrer_id) {
        map.set(g.referrer_id, {
          id: g.referrer_id,
          name: g.referrer_name,
          email: g.referrer_email,
        });
      }
      for (const m of g.members) {
        if (!map.has(m.id)) map.set(m.id, { id: m.id, name: m.name, email: m.email });
      }
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [groups.data]);

  const [inviteReferrer, setInviteReferrer] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteMessage, setInviteMessage] = useState("");
  const [inviteCreateAccount, setInviteCreateAccount] = useState(true);

  const summaryFn = useServerFn(getReferralSummaryStats);
  const summary = useQuery({
    queryKey: ["admin", "referral-summary-stats"],
    queryFn: () => summaryFn({} as never),
    ...resilient,
  });
  const revenueFn = useServerFn(getReferralRevenue);
  const [revenueQ, setRevenueQ] = useState("");
  const revenue = useQuery({
    queryKey: ["admin", "referral-revenue", revenueQ],
    queryFn: () => revenueFn({ data: { q: revenueQ } }),
  });
  const inviteFn = useServerFn(sendReferralInvite);
  const invite = useMutation({
    mutationFn: (vars: {
      referrerId: string;
      email: string;
      message: string;
      createAccount: boolean;
    }) =>
      inviteFn({ data: vars }),
    onSuccess: (res) => {
      if (res.ok) {
        toast.success("Pozvánka byla odeslána.");
        setInviteEmail("");
        setInviteMessage("");
      } else if (res.error === "already_registered") {
        toast.info("Tento e-mail už je zaregistrovaný.");
      } else if (res.error === "recipient_suppressed") {
        toast.warning("Příjemce má zablokované e-maily, pozvánka nebyla doručena.");
      } else if (res.error === "referrer_has_no_code") {
        toast.error("Doporučitel nemá referenční kód.");
      } else {
        toast.error("Pozvánku se nepodařilo odeslat.");
      }
      qc.invalidateQueries({ queryKey: ["admin", "referral-invites"] });
    },
    onError: (e) => toast.error(describeReferralError(e)),
  });

  const resendFn = useServerFn(adminResendConfirmation);
  const resend = useMutation({
    mutationFn: (userId: string) =>
      resendFn({ data: { userId, redirectTo: `${window.location.origin}/moje-podniky` } }),
    onSuccess: (res) => {
      if (res.ok) toast.success(`Potvrzovací e-mail odeslán na ${res.email}.`);
      else toast.info("Uživatel má e-mail už potvrzený.");
    },
    onError: (e) => toast.error(describeReferralError(e)),
  });

  function fmtDate(v: string | null) {
    if (!v) return "—";
    return new Date(v).toLocaleString("cs-CZ", { dateStyle: "short", timeStyle: "short" });
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Struktura klientů a doporučení</h1>
        <p className="text-sm text-muted-foreground">
          Celkem {stats.total} klientů, z toho {stats.noReferrer} bez doporučitele.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Celkem uživatelů</p>
            <p className="text-2xl font-semibold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">S doporučitelem</p>
            <p className="text-2xl font-semibold">{stats.withReferrer}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Bez doporučitele</p>
            <p className="text-2xl font-semibold">{stats.noReferrer}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="flex items-center gap-1 text-xs text-muted-foreground">
              <Percent className="h-3 w-3" /> Registrace přes referenční link
            </p>
            <p className="text-2xl font-semibold">{stats.referredPct} %</p>
            <p className="text-xs text-muted-foreground">
              Potvrzený e-mail: {stats.emailConfirmed} / {stats.total}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Coins className="h-4 w-4" /> Celkové doporučení
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-md border p-3">
              <p className="text-xs text-muted-foreground">Doporučení přinesla celkem</p>
              <p className="text-xl font-semibold">
                {fmtMoney(summary.data?.revenue_cents ?? 0)}
              </p>
              <p className="text-xs text-muted-foreground">
                provize {fmtMoney(summary.data?.commission_cents ?? 0)} ·{" "}
                {summary.data?.referredTotal ?? 0} doporučených
              </p>
            </div>
            <div className="rounded-md border p-3">
              <p className="text-xs text-muted-foreground">Doporučitelů s více než jedním příjmem</p>
              <p className="text-xl font-semibold">
                {summary.data?.referrersWithMultipleIncomes ?? 0}
              </p>
            </div>
            <div className="rounded-md border p-3">
              <p className="text-xs text-muted-foreground">Doporučitelů s jedním příjmem</p>
              <p className="text-xl font-semibold">{summary.data?.referrersWithOneIncome ?? 0}</p>
            </div>
            <div className="rounded-md border p-3">
              <p className="text-xs text-muted-foreground">Doporučitelů bez příjmu</p>
              <p className="text-xl font-semibold">{summary.data?.referrersWithNoIncome ?? 0}</p>
              <p className="text-xs text-muted-foreground">
                z {summary.data?.referrers ?? 0} doporučitelů
              </p>
            </div>
          </div>
        </CardContent>
      </Card>


      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Coins className="h-4 w-4" /> Výnosy podle doporučitele
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <div className="rounded-md border p-3">
              <p className="text-xs text-muted-foreground">Doporučených kandidátů</p>
              <p className="text-xl font-semibold">{revenue.data?.totals.referred ?? 0}</p>
            </div>
            <div className="rounded-md border p-3">
              <p className="text-xs text-muted-foreground">Z toho plátců</p>
              <p className="text-xl font-semibold">
                {revenue.data?.totals.paying ?? 0}{" "}
                <span className="text-sm text-muted-foreground">
                  ({revenue.data?.totals.conversionPct ?? 0} %)
                </span>
              </p>
            </div>
            <div className="rounded-md border p-3">
              <p className="text-xs text-muted-foreground">Příjem z doporučení</p>
              <p className="text-xl font-semibold">
                {fmtMoney(revenue.data?.totals.revenue_cents ?? 0)}
              </p>
            </div>
            <div className="rounded-md border p-3">
              <p className="text-xs text-muted-foreground">% zisku doporučitele</p>
              <p className="text-xl font-semibold">
                {revenue.data?.totals.profitPct ?? 0} %
              </p>
              <p className="text-xs text-muted-foreground">
                {fmtMoney(revenue.data?.totals.commission_cents ?? 0)} celkem
              </p>
            </div>
            <div className="rounded-md border p-3">
              <p className="text-xs text-muted-foreground">Doporučitelů s příjmem</p>
              <p className="text-xl font-semibold">
                {revenue.data?.totals.earningReferrers ?? 0} / {revenue.data?.totals.referrers ?? 0}
              </p>
            </div>
          </div>

          <Input
            className="max-w-sm"
            placeholder="Hledat doporučitele nebo účet…"
            value={revenueQ}
            onChange={(e) => setRevenueQ(e.target.value)}
          />

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Doporučitel</TableHead>
                  <TableHead>ID</TableHead>
                  <TableHead className="text-right">Doporučených</TableHead>
                  <TableHead className="text-right">Plátců</TableHead>
                  <TableHead className="text-right">Konverze</TableHead>
                  <TableHead className="text-right">Příjem</TableHead>
                  <TableHead className="text-right">% zisku</TableHead>
                  <TableHead className="text-right">Provize (čeká / vyplaceno)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {revenue.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-sm text-muted-foreground">
                      Načítám…
                    </TableCell>
                  </TableRow>
                ) : (revenue.data?.rows ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-sm text-muted-foreground">
                      Zatím žádná data.
                    </TableCell>
                  </TableRow>
                ) : (
                  (revenue.data?.rows ?? []).map((r) => (
                    <TableRow key={r.referrer_id}>
                      <TableCell>
                        <button
                          type="button"
                          className="text-left hover:underline"
                          onClick={() => setSelected(r.referrer_id)}
                        >
                          {r.referrer_name}
                        </button>
                        {r.referrer_email ? (
                          <div className="text-xs text-muted-foreground">{r.referrer_email}</div>
                        ) : null}
                      </TableCell>
                      <TableCell><UserId id={r.referrer_id} /></TableCell>
                      <TableCell className="text-right">{r.referred_count}</TableCell>

                      <TableCell className="text-right">{r.paying_count}</TableCell>
                      <TableCell className="text-right">
                        {r.referred_count
                          ? `${Math.round((r.paying_count / r.referred_count) * 1000) / 10} %`
                          : "—"}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {fmtMoney(r.revenue_cents)}
                      </TableCell>
                      <TableCell className="text-right">
                        {r.profit_pct} %
                      </TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground">
                        {fmtMoney(r.commission_pending_cents)} / {fmtMoney(r.commission_paid_cents)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Coins className="h-4 w-4" /> Výnosy podle jednotlivých účtů
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Příjmy, které vytvořil každý konkrétní doporučený účet, a provize z nich.
          </p>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Účet</TableHead>
                  <TableHead>ID</TableHead>
                  <TableHead>Doporučitel</TableHead>
                  <TableHead className="text-right">Platby</TableHead>
                  <TableHead className="text-right">Provize</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {revenue.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-sm text-muted-foreground">
                      Načítám…
                    </TableCell>
                  </TableRow>
                ) : (revenue.data?.rows ?? []).flatMap((r) => r.accounts).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-sm text-muted-foreground">
                      Zatím žádný platící doporučený účet.
                    </TableCell>
                  </TableRow>
                ) : (
                  (revenue.data?.rows ?? [])
                    .flatMap((r) => r.accounts.map((a) => ({ ...a, referrer_name: r.referrer_name, referrer_id: r.referrer_id })))
                    .sort((a, b) => b.paid_cents - a.paid_cents)
                    .map((a) => (
                      <TableRow key={a.user_id}>
                        <TableCell>
                          <button
                            type="button"
                            className="text-left hover:underline"
                            onClick={() => setSelected(a.user_id)}
                          >
                            {a.user_name ?? "—"}
                          </button>
                          {a.user_email ? (
                            <div className="text-xs text-muted-foreground">{a.user_email}</div>
                          ) : null}
                        </TableCell>
                        <TableCell><UserId id={a.user_id} /></TableCell>
                        <TableCell>
                          <button
                            type="button"
                            className="text-left hover:underline"
                            onClick={() => setSelected(a.referrer_id)}
                          >
                            {a.referrer_name}
                          </button>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {fmtMoney(a.paid_cents)}
                        </TableCell>
                        <TableCell className="text-right text-xs text-muted-foreground">
                          {fmtMoney(a.commission_cents)}
                        </TableCell>
                      </TableRow>

                    ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <MailPlus className="h-4 w-4" /> Poslat pozvánku novému kandidátovi
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Vyberte doporučitele a zadejte e-mail kandidáta. Kandidát dostane pozvánku
            s registračním odkazem doporučitele a po registraci se automaticky zařadí
            do struktury pod něj.
          </p>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="invite-referrer">Doporučitel</Label>
              <select
                id="invite-referrer"
                className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                value={inviteReferrer}
                onChange={(e) => setInviteReferrer(e.target.value)}
              >
                <option value="">— vyberte —</option>
                {inviteReferrerOptions.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                    {u.email ? ` (${u.email})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="invite-email">E-mail kandidáta</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="kandidat@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="invite-message">Osobní zpráva (nepovinné)</Label>
              <Input
                id="invite-message"
                placeholder="Krátký text do e-mailu…"
                value={inviteMessage}
                onChange={(e) => setInviteMessage(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="invite-create-account"
              checked={inviteCreateAccount}
              onCheckedChange={(v) => setInviteCreateAccount(v === true)}
            />
            <Label htmlFor="invite-create-account" className="text-sm font-normal">
              Rovnou vytvořit účet a poslat heslo (bez potvrzení e-mailu, přihlášení přímo do
              /moje-podniky)
            </Label>
          </div>
          <Button
            size="sm"
            disabled={!inviteReferrer || !inviteEmail.includes("@") || invite.isPending}
            onClick={() =>
              invite.mutate({
                referrerId: inviteReferrer,
                email: inviteEmail.trim(),
                message: inviteMessage.trim(),
                createAccount: inviteCreateAccount,
              })
            }
          >
            <Send className="mr-2 h-4 w-4" />
            {invite.isPending ? "Odesílám…" : "Odeslat pozvánku"}
          </Button>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Kandidát</TableHead>
                  <TableHead>Doporučitel</TableHead>
                  <TableHead>Stav</TableHead>
                  <TableHead>Odesláno</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(invites.data ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-sm text-muted-foreground">
                      Zatím žádné pozvánky.
                    </TableCell>
                  </TableRow>
                ) : (
                  (invites.data ?? []).map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>{r.email}</TableCell>
                      <TableCell>{r.referrer_name}</TableCell>
                      <TableCell>
                        <Badge variant={r.status === "accepted" ? "default" : r.status === "sent" ? "secondary" : "destructive"}>
                          {r.status === "accepted"
                            ? "Zaregistrován"
                            : r.status === "sent"
                              ? "Odesláno"
                              : r.status === "suppressed"
                                ? "Blokováno"
                                : "Chyba"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {fmtDate(r.sent_at ?? r.created_at)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Layers className="h-4 w-4" /> Struktura podle doporučitele
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input
            className="max-w-sm"
            placeholder="Hledat doporučitele nebo člena…"
            value={groupQ}
            onChange={(e) => setGroupQ(e.target.value)}
          />
          {groups.isLoading ? (
            <p className="text-sm text-muted-foreground">Načítám…</p>
          ) : groups.isError ? (
            <div className="space-y-2">
              <p className="text-sm text-destructive">
                Strukturu se nepodařilo načíst: {(groups.error as Error).message}
              </p>
              <Button size="sm" variant="outline" onClick={() => groups.refetch()}>
                Zkusit znovu
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {(groups.data?.groups ?? []).map((g) => {
                const key = g.referrer_id ?? "__none__";
                const open = openGroups.has(key);
                return (
                  <div key={key} className="rounded-md border">
                    <button
                      type="button"
                      className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
                      onClick={() =>
                        setOpenGroups((prev) => {
                          const next = new Set(prev);
                          if (next.has(key)) next.delete(key);
                          else next.add(key);
                          return next;
                        })
                      }
                    >
                      <span className="flex items-center gap-2 text-sm font-medium">
                        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        {g.referrer_name}
                        {g.referrer_email ? (
                          <span className="text-xs text-muted-foreground">{g.referrer_email}</span>
                        ) : null}
                      </span>
                      <span className="flex items-center gap-2">
                        <Badge variant="secondary">Přímo: {g.direct_count}</Badge>
                        <Badge variant="outline">Celkem pod ním: {g.subtree_count}</Badge>
                      </span>
                    </button>
                    {open ? (
                      <ul className="border-t px-4 py-2 text-sm">
                        {g.members.map((m) => (
                          <li key={m.id} className="py-1">
                            <div className="flex items-center justify-between">
                              <button
                                type="button"
                                className="text-left hover:underline"
                                onClick={() => setSelected(m.id)}
                              >
                                {m.name}
                                {m.email ? (
                                  <span className="ml-2 text-xs text-muted-foreground">{m.email}</span>
                                ) : null}
                                <span className="ml-2 block sm:inline"><UserId id={m.id} /></span>
                              </button>
                              <span className="text-xs text-muted-foreground">Vacantů: {m.vakanty}</span>
                            </div>
                            {!g.referrer_id && (
                              <AssignInline
                                pending={assign.isPending}
                                onAssign={(referrerId) => assign.mutate({ userId: m.id, referrerId })}
                              />
                            )}
                          </li>
                        ))}

                      </ul>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <StripeSyncCard />

      <Card>

        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="h-4 w-4" /> Seznam klientů
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Input
              className="max-w-xs"
              placeholder="Hledat podle jména, e-mailu nebo doporučitele…"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
            />
            <div className="flex items-center gap-2">
              <Switch
                id="no-referrer"
                checked={onlyNoReferrer}
                onCheckedChange={(v) => {
                  setOnlyNoReferrer(v);
                  if (v) setReferrerFilter(null);
                  setPage(1);
                }}
              />
              <Label htmlFor="no-referrer">Jen bez doporučitele</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="for-direct"
                checked={referrerFilter === FOR_DIRECT_REFERRER_ID}
                onCheckedChange={(v) => {
                  setReferrerFilter(v ? FOR_DIRECT_REFERRER_ID : null);
                  if (v) setOnlyNoReferrer(false);
                  setPage(1);
                }}
              />
              <Label htmlFor="for-direct">Doporučitel "for direct"</Label>
            </div>
            <div className="flex items-center gap-1">
              <Label htmlFor="src-filter" className="text-xs text-muted-foreground">Zdroj návštěv</Label>
              <select
                id="src-filter"
                className="h-9 rounded-md border bg-background px-2 text-sm"
                value={sourceFilter}
                onChange={(e) => {
                  setSourceFilter(e.target.value as SourceFilter);
                  setPage(1);
                }}
              >
                <option value="any">Všechny</option>
                <option value="app">Aplikace</option>
                <option value="chat">💬 Chat</option>
                <option value="email">✉ E-mail</option>
                <option value="referral">Referral odkaz</option>
              </select>
            </div>
            <div className="flex items-center gap-1">
              <Label htmlFor="visit-filter" className="text-xs text-muted-foreground">Aktivace</Label>
              <select
                id="visit-filter"
                className="h-9 rounded-md border bg-background px-2 text-sm"
                value={visitFilter}
                onChange={(e) => {
                  setVisitFilter(e.target.value as VisitFilter);
                  setPage(1);
                }}
              >
                <option value="any">Vše</option>
                <option value="visited">Má první návštěvu</option>
                <option value="never">Jen registrace (bez návštěvy)</option>
              </select>
            </div>
            <Input
              className="w-36"
              inputMode="decimal"
              placeholder="Min. příjem €"
              value={minRevenue}
              onChange={(e) => {
                setMinRevenue(e.target.value);
                setPage(1);
              }}
            />
            {(sourceFilter !== "any" || visitFilter !== "any" || minRevenue || referrerFilter) && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSourceFilter("any");
                  setVisitFilter("any");
                  setMinRevenue("");
                  setReferrerFilter(null);
                  setPage(1);
                }}
              >
                Zrušit filtry
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={exportCsv}>
              <Download className="mr-2 h-4 w-4" /> Export CSV
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 p-3">
            <Wand2 className="h-4 w-4" />
            <span className="text-sm font-medium">
              Hromadná oprava doporučitelů — vybráno {bulkIds.size}
            </span>
            <Input
              className="max-w-sm"
              placeholder="ID profilu nového doporučitele (UUID)"
              value={bulkReferrer}
              onChange={(e) => setBulkReferrer(e.target.value)}
            />
            <Button
              size="sm"
              disabled={!bulkIds.size || !bulkReferrer.trim() || bulkAssign.isPending}
              onClick={() =>
                bulkAssign.mutate({
                  userIds: [...bulkIds],
                  referrerId: bulkReferrer.trim(),
                })
              }
            >
              Nastavit vybraným
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!bulkIds.size || bulkAssign.isPending}
              onClick={() => bulkAssign.mutate({ userIds: [...bulkIds], referrerId: null })}
            >
              Odebrat vybraným
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={!bulkIds.size}
              onClick={() => setBulkIds(new Set())}
            >
              Zrušit výběr
            </Button>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">
                    <Checkbox
                      aria-label="Vybrat vše na stránce"
                      checked={
                        !!clients.data?.rows.length &&
                        clients.data.rows.every((r: ClientRow) => bulkIds.has(r.id))
                      }
                      onCheckedChange={(v) => {
                        const ids = (clients.data?.rows ?? []).map((r: ClientRow) => r.id);
                        setBulkIds((prev) => {
                          const next = new Set(prev);
                          if (v) ids.forEach((id) => next.add(id));
                          else ids.forEach((id) => next.delete(id));
                          return next;
                        });
                      }}
                    />
                  </TableHead>
                  <TableHead>
                    <button className="inline-flex items-center gap-1" onClick={() => toggleSort("name")}>
                      Jméno <ArrowUpDown className="h-3 w-3" />
                    </button>
                  </TableHead>
                  <TableHead>
                    <button className="inline-flex items-center gap-1" onClick={() => toggleSort("email")}>
                      E-mail <ArrowUpDown className="h-3 w-3" />
                    </button>
                  </TableHead>
                  <TableHead>ID</TableHead>
                  <TableHead className="text-right">
                    <button className="inline-flex items-center gap-1" onClick={() => toggleSort("vakanty")}>
                      Vacanty <ArrowUpDown className="h-3 w-3" />
                    </button>
                  </TableHead>
                  <TableHead>Zařazení</TableHead>
                  <TableHead>Doporučitel</TableHead>
                  <TableHead className="text-right">Přímí</TableHead>
                  <TableHead className="text-right">
                    <button className="inline-flex items-center gap-1" onClick={() => toggleSort("visits")}>
                      Návštěvy <ArrowUpDown className="h-3 w-3" />
                    </button>
                  </TableHead>
                  <TableHead>
                    <button className="inline-flex items-center gap-1" onClick={() => toggleSort("first_visit")}>
                      První návštěva <ArrowUpDown className="h-3 w-3" />
                    </button>
                  </TableHead>
                  <TableHead className="text-right">
                    <button className="inline-flex items-center gap-1" onClick={() => toggleSort("revenue")}>
                      Příjem <ArrowUpDown className="h-3 w-3" />
                    </button>
                  </TableHead>

                  <TableHead>Potvrzený e-mail</TableHead>
                  <TableHead />
                </TableRow>

              </TableHeader>
              <TableBody>
                {clients.isLoading && (
                  <TableRow>
                    <TableCell colSpan={13}>Načítám…</TableCell>
                  </TableRow>
                )}
                {clients.data?.rows.map((r: ClientRow) => (
                  <TableRow key={r.id} data-state={selected === r.id ? "selected" : undefined}>
                    <TableCell>
                      <Checkbox
                        aria-label={`Vybrat ${r.full_name ?? r.email ?? r.id}`}
                        checked={bulkIds.has(r.id)}
                        onCheckedChange={(v) =>
                          setBulkIds((prev) => {
                            const next = new Set(prev);
                            if (v) next.add(r.id);
                            else next.delete(r.id);
                            return next;
                          })
                        }
                      />
                    </TableCell>
                    <TableCell className="font-medium">{r.full_name ?? "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{r.email ?? "—"}</TableCell>
                    <TableCell><UserId id={r.id} /></TableCell>

                    <TableCell className="text-right">{r.vakanty_purchased}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {r.roles.length ? (
                          r.roles.map((role) => (
                            <Badge key={role} variant="secondary">
                              {role}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {r.referrer_name ?? (
                        <Badge variant="outline">bez doporučitele</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">{r.direct_children}</TableCell>
                    <TableCell className="text-right">
                      {r.visits_count > 0 ? (
                        <span
                          title={`aplikace ${r.visits_by_source?.app ?? 0} · chat ${r.visits_by_source?.chat ?? 0} · e-mail ${r.visits_by_source?.email ?? 0} · odkaz ${r.visits_by_source?.referral ?? 0}`}
                        >
                          {r.visits_count}
                          {(r.visits_by_source?.chat ?? 0) + (r.visits_by_source?.email ?? 0) > 0 && (
                            <span className="ml-1 text-xs text-muted-foreground">
                              ({(r.visits_by_source?.chat ?? 0) > 0 ? `💬${r.visits_by_source.chat}` : ""}
                              {(r.visits_by_source?.email ?? 0) > 0 ? ` ✉${r.visits_by_source.email}` : ""})
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </TableCell>

                    <TableCell className="whitespace-nowrap text-sm">
                      {r.first_visit_at ? (
                        <span title={new Date(r.first_visit_at).toLocaleString("cs-CZ")}>
                          {new Date(r.first_visit_at).toLocaleDateString("cs-CZ")}
                        </span>
                      ) : (
                        <Badge variant="outline">jen registrace</Badge>
                      )}
                    </TableCell>

                    <TableCell className="text-right">
                      {r.revenue_cents > 0 ? (
                        fmtMoney(r.revenue_cents)
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>

                    <TableCell>
                      {r.email_confirmed_at ? (
                        <span className="inline-flex items-center gap-1 text-sm">
                          <MailCheck className="h-3.5 w-3.5 text-primary" />
                          {fmtDate(r.email_confirmed_at)}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-2">
                          <Badge variant="outline" className="gap-1">
                            <MailWarning className="h-3 w-3" /> nepotvrzeno
                          </Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={resend.isPending || !r.email}
                            onClick={() => resend.mutate(r.id)}
                          >
                            <Send className="mr-1 h-3 w-3" /> Odeslat znovu
                          </Button>
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => setSelected(r.id)}>
                        Strom
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {clients.data && clients.data.rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10}>Žádné výsledky.</TableCell>
                  </TableRow>
                )}

              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              Stránka {page} z {pages} — {total} záznamů
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Předchozí
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= pages}
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
              >
                Další
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <ReferrerMessagesCard />
      <ReferrerContributionsCard />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <UserSearch className="h-4 w-4" /> Kandidáti bez doporučitele
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Uživatelé bez doporučitele. U {candidates.data?.withSuggestion ?? 0} z{" "}
            {candidates.data?.total ?? 0} lze doporučitele dohledat z pozvánek — přiřadíte ho
            jedním kliknutím.
          </p>
          {candidates.isError && (
            <div className="space-y-2">
              <p className="text-sm text-destructive">
                Seznam se nepodařilo načíst: {(candidates.error as Error).message}
              </p>
              <Button size="sm" variant="outline" onClick={() => candidates.refetch()}>
                Zkusit znovu
              </Button>
            </div>
          )}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Jméno</TableHead>
                  <TableHead>E-mail</TableHead>
                  <TableHead>ID</TableHead>
                  <TableHead className="text-right">Vacanty</TableHead>
                  <TableHead>Registrace</TableHead>
                  <TableHead>Potvrzený e-mail</TableHead>
                  <TableHead>Navržený doporučitel</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {candidates.isLoading && (
                  <TableRow>
                    <TableCell colSpan={8}>Načítám…</TableCell>
                  </TableRow>
                )}
                {candidates.data?.rows.map((c: ReferralCandidate) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.full_name ?? "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{c.email ?? "—"}</TableCell>
                    <TableCell><UserId id={c.id} /></TableCell>
                    <TableCell className="text-right">{c.vakanty_purchased}</TableCell>
                    <TableCell className="text-sm">{fmtDate(c.created_at)}</TableCell>
                    <TableCell className="text-sm">
                      {c.email_confirmed_at ? (
                        fmtDate(c.email_confirmed_at)
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={resend.isPending || !c.email}
                          onClick={() => resend.mutate(c.id)}
                        >
                          <Send className="mr-1 h-3 w-3" /> Odeslat potvrzení
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      {c.suggested_referrer_name ? (
                        <span className="text-sm">
                          {c.suggested_referrer_name}{" "}
                          <Badge variant="secondary">z pozvánky</Badge>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        disabled={!c.suggested_referrer_id || assign.isPending}
                        onClick={() =>
                          assign.mutate({
                            userId: c.id,
                            referrerId: c.suggested_referrer_id!,
                          })
                        }
                      >
                        Přiřadit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {candidates.data && candidates.data.rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>Všichni uživatelé mají doporučitele.</TableCell>
                  </TableRow>
                )}

              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="h-4 w-4" /> Uživatelé s doporučitelem „for direct"
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Uživatelé, jejichž doporučitelem je účet „for direct" ({forDirect.data?.rows.length ?? 0}).
          </p>
          {forDirect.isError && (
            <div className="space-y-2">
              <p className="text-sm text-destructive">
                Seznam se nepodařilo načíst: {(forDirect.error as Error).message}
              </p>
              <Button size="sm" variant="outline" onClick={() => forDirect.refetch()}>
                Zkusit znovu
              </Button>
            </div>
          )}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Jméno</TableHead>
                  <TableHead>E-mail</TableHead>
                  <TableHead>ID</TableHead>
                  <TableHead className="text-right">Vacanty</TableHead>
                  <TableHead>Registrace</TableHead>
                  <TableHead className="text-right">Návštěvy</TableHead>
                  <TableHead className="text-right">Příjem</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {forDirect.isLoading && (
                  <TableRow>
                    <TableCell colSpan={8}>Načítám…</TableCell>
                  </TableRow>
                )}
                {forDirect.data?.rows.map((r: ClientRow) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.full_name ?? "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{r.email ?? "—"}</TableCell>
                    <TableCell><UserId id={r.id} /></TableCell>
                    <TableCell className="text-right">{r.vakanty_purchased}</TableCell>
                    <TableCell className="text-sm">{fmtDate(r.created_at)}</TableCell>
                    <TableCell className="text-right">{r.visits_count}</TableCell>
                    <TableCell className="text-right">
                      {r.revenue_cents > 0 ? fmtMoney(r.revenue_cents) : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => setSelected(r.id)}>
                        Strom
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {forDirect.data && forDirect.data.rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>Žádní uživatelé s doporučitelem „for direct".</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Network className="h-4 w-4" /> Strom struktury registrovaných uživatelů
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Input
              className="max-w-xs"
              placeholder="Filtrovat ve stromu (jméno / e-mail)…"
              value={forestQ}
              onChange={(e) => setForestQ(e.target.value)}
            />
            <Button size="sm" variant="outline" onClick={() => setExpandAll((v) => !v)}>
              {expandAll ? "Sbalit vše" : "Rozbalit vše"}
            </Button>
            {forest.data && (
              <span className="text-sm text-muted-foreground">
                {forest.data.totalUsers} uživatelů · {structureRoots.length} kořenů struktury ·{" "}
                {unassignedRoots.length} bez přiřazení
              </span>
            )}
          </div>

          {forest.isLoading && <p className="text-sm">Načítám strukturu…</p>}
          {forest.error && (
            <p className="text-sm text-destructive">{(forest.error as Error).message}</p>
          )}
          {visibleRoots && (
            <>
              <div>
                <h3 className="mb-2 text-sm font-semibold">Struktura (kořeny s podstromem)</h3>
                <div className="max-h-[60vh] overflow-auto rounded-md border p-3">
                  {structureRoots.length ? (
                    structureRoots.map((n) => (
                      <ForestBranch
                        key={n.id}
                        node={n}
                        level={0}
                        defaultOpen={expandAll || !!forestQ.trim()}
                        onSelect={setSelected}
                      />
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">Žádná větev struktury neodpovídá filtru.</p>
                  )}
                </div>
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold">
                  Uživatelé bez přiřazení
                  <Badge variant="outline" className="ml-2">{unassignedRoots.length}</Badge>
                </h3>
                <p className="mb-2 text-xs text-muted-foreground">
                  Nemají doporučitele ani vlastní podstrom. Kliknutím otevřete detail a přiřadíte je do struktury.
                </p>
                <div className="max-h-[40vh] overflow-auto rounded-md border p-3">
                  {unassignedRoots.length ? (
                    <ul className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
                        {unassignedRoots.map((n) => (
                          <li key={n.id} className="rounded border px-2 py-1">
                            <button
                              type="button"
                              onClick={() => setSelected(n.id)}
                              className="w-full truncate rounded text-left text-sm hover:bg-muted"
                              title={n.email ?? undefined}
                            >
                              {n.full_name ?? n.email ?? n.id}
                              <span className="ml-1 text-xs text-muted-foreground">
                                · {n.vakanty_purchased} vac.
                              </span>
                              <span className="block"><UserId id={n.id} /></span>
                            </button>
                            <AssignInline
                              pending={assign.isPending}
                              onAssign={(referrerId) => assign.mutate({ userId: n.id, referrerId })}
                            />
                          </li>
                        ))}


                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Všichni uživatelé jsou někam přiřazeni (nebo mají vlastní strukturu).
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>



      {selected && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <GitBranch className="h-4 w-4" /> Strom doporučení
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {treeQuery.isLoading && <p>Načítám strom…</p>}
            {treeQuery.data && (
              <>
                <div className="text-sm text-muted-foreground">
                  {treeQuery.data.directChildren} přímých doporučených,{" "}
                  {treeQuery.data.totalDescendants} celkem v podstromu.
                </div>

                <div>
                  <h3 className="mb-2 text-sm font-semibold">Nadřazení (upline)</h3>
                  {treeQuery.data.ancestors.length ? (
                    <ol className="flex flex-wrap items-center gap-2 text-sm">
                      {[...treeQuery.data.ancestors].reverse().map((a) => (
                        <li key={a.id} className="flex items-center gap-2">
                          <button
                            className="rounded border px-2 py-1 text-left hover:bg-muted"
                            onClick={() => setSelected(a.id)}
                          >
                            {a.full_name ?? a.email ?? a.id}
                            <span className="block"><UserId id={a.id} /></span>
                          </button>
                          <span className="text-muted-foreground">→</span>
                        </li>
                      ))}
                      <li className="rounded border border-primary px-2 py-1 font-medium">
                        {treeQuery.data.self.full_name ?? treeQuery.data.self.email}
                        <span className="block font-normal"><UserId id={treeQuery.data.self.id} /></span>
                      </li>
                    </ol>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Bez doporučitele — kořen struktury.
                    </p>
                  )}
                </div>


                <div>
                  <h3 className="mb-2 text-sm font-semibold">Sousední vazby (sourozenci)</h3>
                  {treeQuery.data.siblings.length ? (
                    <div className="flex flex-wrap gap-2">
                      {treeQuery.data.siblings.map((s) => (
                        <button
                          key={s.id}
                          className="rounded border px-2 py-1 text-left text-sm hover:bg-muted"
                          onClick={() => setSelected(s.id)}
                        >
                          {s.full_name ?? s.email} · {s.vakanty_purchased} vac.
                          <span className="block"><UserId id={s.id} /></span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Žádní sourozenci.</p>
                  )}
                </div>


                <div>
                  <h3 className="mb-2 text-sm font-semibold">Podstrom</h3>
                  <TreeView node={treeQuery.data.tree} onSelect={setSelected} />
                </div>

                {(activationDiag.data?.length ?? 0) > 0 && (
                  <div className="rounded-md border border-amber-300/60 bg-amber-50/50 p-3 dark:bg-amber-950/20">
                    <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
                      <AlertTriangle className="h-4 w-4" /> Čekající platby — důvod neaktivace
                    </h3>
                    <ul className="space-y-2 text-sm">
                      {activationDiag.data!.map((d) => (
                        <li key={d.orderId ?? d.refId} className="flex flex-col gap-0.5">
                          <span className="font-medium">
                            {d.label} · {fmtMoney(d.amountCents)}
                            {d.createdAt && ` · ${new Date(d.createdAt).toLocaleDateString("cs-CZ")}`}
                          </span>
                          <span className={d.severity === "error" ? "text-destructive" : "text-muted-foreground"}>
                            {d.reason}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="rounded-md border p-3">
                  <h3 className="mb-2 text-sm font-semibold">
                    {treeQuery.data.ancestors.length ? "Změnit doporučitele" : "Přiřadit doporučitele"}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    <Input
                      className="max-w-sm"
                      placeholder="ID profilu doporučitele (UUID)"
                      value={referrerInput}
                      onChange={(e) => setReferrerInput(e.target.value)}
                    />
                    <Button
                      size="sm"
                      disabled={!referrerInput.trim() || assign.isPending}
                      onClick={() =>
                        assign.mutate({ userId: selected, referrerId: referrerInput.trim() })
                      }
                    >
                      Uložit
                    </Button>
                    {treeQuery.data.ancestors.length > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={assign.isPending}
                        onClick={() => assign.mutate({ userId: selected, referrerId: null })}
                      >
                        Odebrat doporučitele
                      </Button>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Admin může doporučitele kdykoli změnit nebo odebrat; každá změna se zapíše do
                    audit logu. Cyklické vazby nejsou povoleny.
                  </p>
                </div>

              </>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <History className="h-4 w-4" /> Audit log změn doporučení
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Kdy</TableHead>
                  <TableHead>Kdo</TableHead>
                  <TableHead>Akce</TableHead>
                  <TableHead>Cíl</TableHead>
                  <TableHead>Detail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {audit.data?.length ? (
                  audit.data.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell className="whitespace-nowrap">
                        {new Date(a.created_at).toLocaleString("cs-CZ")}
                      </TableCell>
                      <TableCell>{a.admin_name}</TableCell>
                      <TableCell>{a.action}</TableCell>
                      <TableCell>{a.target_email ?? "—"}</TableCell>
                      <TableCell className="max-w-md truncate text-muted-foreground">
                        {[
                          a.details.user_name ?? a.target_email,
                          a.details.referrer_name ?? a.details.referrer_email,
                        ]
                          .filter(Boolean)
                          .join(" → ") || "—"}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5}>Zatím žádné záznamy.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function TreeView({ node, onSelect }: { node: TreeNode; onSelect: (id: string) => void }) {
  return (
    <ul className="ml-4 border-l pl-4 text-sm">
      <li>
        <button className="rounded px-1 hover:bg-muted" onClick={() => onSelect(node.id)}>
          <span className="font-medium">{node.full_name ?? node.email ?? node.id}</span>{" "}
          <span className="text-muted-foreground">· {node.vakanty_purchased} vac.</span>
        </button>{" "}
        <UserId id={node.id} />

        {node.children.length > 0 && (
          <div className="mt-1 space-y-1">
            {node.children.map((c) => (
              <TreeView key={c.id} node={c} onSelect={onSelect} />
            ))}
          </div>
        )}
      </li>
    </ul>
  );
}

function ForestBranch({
  node,
  level,
  defaultOpen,
  onSelect,
}: {
  node: ForestNode;
  level: number;
  defaultOpen: boolean;
  onSelect: (id: string) => void;
}) {
  const [open, setOpen] = useState(defaultOpen || level === 0);
  useEffect(() => {
    setOpen(defaultOpen || level === 0);
  }, [defaultOpen, level]);
  const hasKids = node.children.length > 0;
  return (
    <div style={{ marginLeft: level === 0 ? 0 : 16 }} className={level ? "border-l pl-3" : ""}>
      <div className="flex items-center gap-1 py-0.5 text-sm">
        {hasKids ? (
          <button
            className="rounded p-0.5 hover:bg-muted"
            aria-label={open ? "Sbalit" : "Rozbalit"}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        ) : (
          <span className="inline-block w-[1.125rem]" />
        )}
        <button className="rounded px-1 hover:bg-muted" onClick={() => onSelect(node.id)}>
          <span className="font-medium">{node.full_name ?? node.email ?? node.id}</span>
        </button>
        <UserId id={node.id} />

        <span className="text-xs text-muted-foreground">
          {node.email && node.full_name ? `· ${node.email} ` : ""}· {node.vakanty_purchased} vac.
          {hasKids ? ` · ${node.children.length} přímých / ${node.descendants} celkem` : ""}
        </span>
        {node.roles.map((r) => (
          <Badge key={r} variant="secondary" className="text-[10px]">
            {r}
          </Badge>
        ))}
      </div>
      {open && hasKids && (
        <div>
          {node.children.map((c) => (
            <ForestBranch key={c.id} node={c} level={level + 1} defaultOpen={defaultOpen} onSelect={onSelect} />
          ))}
        </div>
      )}
    </div>
  );
}
